import { useState } from 'react'
import SearchBar from '../../components/ui/SearchBar'
import ServiceCard from '../../components/ui/ServiceCard'
import { Button } from '../../components/ui/button'
import { Skeleton } from '../../components/ui/skeleton'
import { BrandMark } from '../../components/BrandMark'
import { ThemeToggle } from '../../components/ThemeToggle'
import { useServices } from '../../hooks/useServices'
import { FILTER_CATEGORIES, categoryLabel } from 'shared'

const CustomerHome = () => {
  const [query, setQuery] = useState('')
  const [category, setCategory] = useState('All')
  const [page, setPage] = useState(1)

  const { data, isLoading, isError, isPlaceholderData, refetch } = useServices({ q: query, category, page })

  const handleSearch = (value: string) => {
    setQuery(value)
    setPage(1)
  }

  const handleCategory = (c: string) => {
    setCategory(c)
    setPage(1)
  }

  const services = data?.services ?? []
  const pagination = data?.pagination

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="sticky top-0 z-10 bg-background/70 backdrop-blur supports-[backdrop-filter]:bg-background/50 border-b border-border">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center gap-4">
          <BrandMark to="/enter" />
          <div className="flex-1">
            <SearchBar onSearch={handleSearch} onFilterClick={() => {}} />
          </div>
          <ThemeToggle />
        </div>
        <div className="max-w-7xl mx-auto px-4 pb-3 overflow-x-auto">
          <div className="flex items-center gap-2 w-max">
            {FILTER_CATEGORIES.map((c) => (
              <Button
                key={c.value}
                size="sm"
                variant={category === c.value ? 'default' : 'outline'}
                className="rounded-full"
                onClick={() => handleCategory(c.value)}
              >
                {c.label}
              </Button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        <h2 className="text-xl font-semibold mb-4">Popular near you</h2>

        {isLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} className="h-72 rounded-2xl" />
            ))}
          </div>
        ) : isError ? (
          <div className="flex flex-col items-center gap-3 py-16 text-center">
            <p className="text-muted-foreground">Couldn’t load services.</p>
            <Button onClick={() => refetch()}>Retry</Button>
          </div>
        ) : services.length === 0 ? (
          <div className="py-16 text-center text-muted-foreground">
            <p>No services found{query ? ` for “${query}”` : ''}{category !== 'All' ? ` in ${categoryLabel(category)}` : ''}.</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {services.map((s) => (
                <ServiceCard key={s.id} service={s} />
              ))}
            </div>

            {pagination && pagination.totalPages > 1 && (
              <div className="flex items-center justify-center gap-4 mt-8">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1 || isPlaceholderData}
                  onClick={() => setPage((p) => p - 1)}
                >
                  Previous
                </Button>
                <span className="text-sm text-muted-foreground">
                  Page {pagination.page} of {pagination.totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= pagination.totalPages || isPlaceholderData}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default CustomerHome
